import { useEffect, useMemo, useRef, useState } from 'react';
import { Container, Box, Grid, CardContent } from '@mui/material';
import {
  ShoppingCartOutlined,
  Add as AddIcon,
  Remove as RemoveIcon,
  ArrowBackIosNew,
  ArrowForwardIos,
  AutoAwesome,
  Inventory2Outlined,
  SpaOutlined,
  VerifiedOutlined,
} from '@mui/icons-material';
import CheckIcon from '@mui/icons-material/Check';
import { useDispatch, useSelector } from 'react-redux';

import { RootState } from '../../../redux';
import { addToCart, openCart } from '../../../redux/cart';
import { ProductModel } from '../../models/product/product';
import { productBullets, savingsPercent, unitPriceFor } from '../../../api/product';
import { resolveImage } from '../../../api/axiosIntance';
import Reveal from '../../atome/motion/reveal';
import ImageTape from '../../atome/scroll/scroll';
import {
  CategoryTag,
  CheckoutButton,
  CurrentPrice,
  Divider,
  FloatingBadge,
  Frame,
  Gallery,
  GalleryNav,
  LineWrapper,
  Orb,
  OriginalPrice,
  Price,
  PriceHint,
  ProductCard,
  ProductDescription,
  ProductSection,
  ProductTitle,
  QtyButton,
  QtyValue,
  QuantitySelector,
  SaveChip,
  Slide,
  SoloHeading,
  SoloRibbon,
  SoloSubheading,
  StockPill,
  TapeCaption,
  Thumb,
  Thumbs,
  TrustRow,
} from './product.styled';

/** Fallback art for products that have no uploads yet. */
const FALLBACK_IMAGES = ['/img/klarity-product.jpg', '/img/klarity-product2.jpg'];

const BEFORE_AFTER = [
  '/img/image3.png',
  '/img/image1.png',
  '/img/image2.png',
  '/img/image4.png',
  '/img/image5.png',
  '/img/image6.png',
  '/img/image7.png',
];

interface ProductShowcaseProps {
  product: ProductModel;
  /** `solo` is the spotlight treatment used when the shop carries one product. */
  variant?: 'solo' | 'detail';
}

const ProductShowcase = ({ product, variant = 'solo' }: ProductShowcaseProps) => {
  const dispatch = useDispatch();
  const userEmail = useSelector((state: RootState) => state.user.email);

  const [quantity, setQuantity] = useState(1);
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const isSolo = variant === 'solo';

  const images = useMemo(() => {
    const fromApi = (product.imageUrl || []).map(resolveImage).filter(Boolean);
    return fromApi.length ? fromApi : FALLBACK_IMAGES;
  }, [product.imageUrl]);

  const bullets = useMemo(() => productBullets(product.description), [product.description]);

  const hasEmail = Boolean(userEmail);
  const unit = unitPriceFor(product, hasEmail);
  const saving = savingsPercent(product, hasEmail);
  const lineTotal = unit * quantity;
  const soldOut = product.stock === 0;
  const maxQty = product.stock > 0 ? product.stock : 1;

  // Reset the gallery whenever a different product is shown.
  const productId = product.id;
  useEffect(() => {
    setCurrent(0);
    setQuantity(1);
  }, [productId]);

  // Gentle auto-advance, paused while the visitor is inspecting an image.
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (images.length < 2 || paused) return;
    timer.current = setInterval(() => setCurrent((i) => (i + 1) % images.length), 6000);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [images.length, paused]);

  const go = (step: number) => setCurrent((i) => (i + step + images.length) % images.length);

  const changeQuantity = (delta: number) =>
    setQuantity((q) => Math.min(maxQty, Math.max(1, q + delta)));

  // Adding opens the bag rather than jumping to checkout, so a visitor can
  // keep browsing and order several products in one go.
  const handleAddToCart = () => {
    dispatch(addToCart({ product, quantity }));
    dispatch(openCart());
  };

  return (
    <ProductSection id="product-section" $solo={isSolo}>
      {isSolo && (
        <>
          <Orb $size="320px" $top="6%" $left="-6%" />
          <Orb $size="220px" $top="62%" $left="88%" $delay="1.4s" />
        </>
      )}

      <Container maxWidth="lg">
        {isSolo && (
          <Reveal from="up">
            <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
              <SoloRibbon>
                <AutoAwesome sx={{ fontSize: 15 }} />
                The Klarity Signature
              </SoloRibbon>
              <SoloHeading variant="h2">{product.name}</SoloHeading>
              <SoloSubheading>
                One formula, perfected. Everything we make lives in this single jar — so every
                ingredient had to earn its place.
              </SoloSubheading>
            </Box>
          </Reveal>
        )}

        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <Reveal from="left">
              <Gallery>
                <Frame
                  $solo={isSolo}
                  onMouseEnter={() => setPaused(true)}
                  onMouseLeave={() => setPaused(false)}
                >
                  {images.map((src, i) => (
                    <Slide
                      key={src + i}
                      src={src}
                      alt={`${product.name} — view ${i + 1}`}
                      $active={i === current}
                      loading={i === 0 ? 'eager' : 'lazy'}
                      onError={(e) => {
                        e.currentTarget.src = FALLBACK_IMAGES[0];
                      }}
                    />
                  ))}

                  {product.discount > 0 && <FloatingBadge>-{product.discount}% today</FloatingBadge>}

                  {product.stock <= 5 && (
                    <StockPill $tone={soldOut ? 'out' : 'low'}>
                      {soldOut ? 'Sold out' : `Only ${product.stock} left`}
                    </StockPill>
                  )}

                  {images.length > 1 && (
                    <>
                      <GalleryNav $side="left" onClick={() => go(-1)} aria-label="Previous image">
                        <ArrowBackIosNew sx={{ fontSize: 16 }} />
                      </GalleryNav>
                      <GalleryNav $side="right" onClick={() => go(1)} aria-label="Next image">
                        <ArrowForwardIos sx={{ fontSize: 16 }} />
                      </GalleryNav>
                    </>
                  )}
                </Frame>

                {images.length > 1 && (
                  <Thumbs>
                    {images.map((src, i) => (
                      <Thumb
                        key={`thumb-${i}`}
                        $active={i === current}
                        onClick={() => setCurrent(i)}
                        aria-label={`Show image ${i + 1}`}
                      >
                        <img
                          src={src}
                          alt=""
                          onError={(e) => {
                            e.currentTarget.src = FALLBACK_IMAGES[0];
                          }}
                        />
                      </Thumb>
                    ))}
                  </Thumbs>
                )}
              </Gallery>
            </Reveal>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Reveal from="right" delay={120}>
              <ProductCard elevation={0}>
                <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                  {product.category && <CategoryTag>{product.category}</CategoryTag>}
                  <ProductTitle variant="h3">{product.name}</ProductTitle>

                  <ProductDescription as="div">
                    {bullets.map((line, i) => (
                      <LineWrapper key={i}>
                        <CheckIcon sx={{ color: '#4a7061', fontSize: 18 }} />
                        <span>{line}</span>
                      </LineWrapper>
                    ))}
                  </ProductDescription>

                  <Divider />

                  <Price>
                    <CurrentPrice>${lineTotal.toFixed(2)}</CurrentPrice>
                    {saving > 0 && (
                      <>
                        <OriginalPrice>${(product.price * quantity).toFixed(2)}</OriginalPrice>
                        <SaveChip>Save {saving}%</SaveChip>
                      </>
                    )}
                  </Price>
                  <PriceHint>
                    {quantity > 1 ? `$${unit.toFixed(2)} each · ` : ''}
                    {saving > 0
                      ? 'Discount applied automatically at checkout.'
                      : 'Add your email at checkout and save 10%.'}
                  </PriceHint>

                  <QuantitySelector>
                    <QtyButton
                      onClick={() => changeQuantity(-1)}
                      disabled={quantity <= 1}
                      aria-label="Decrease quantity"
                    >
                      <RemoveIcon sx={{ fontSize: 18 }} />
                    </QtyButton>
                    <QtyValue>{quantity}</QtyValue>
                    <QtyButton
                      onClick={() => changeQuantity(1)}
                      disabled={quantity >= maxQty}
                      aria-label="Increase quantity"
                    >
                      <AddIcon sx={{ fontSize: 18 }} />
                    </QtyButton>
                  </QuantitySelector>

                  <CheckoutButton
                    variant="contained"
                    fullWidth
                    size="large"
                    disableElevation
                    startIcon={<ShoppingCartOutlined />}
                    onClick={handleAddToCart}
                    disabled={soldOut}
                  >
                    {soldOut ? 'Sold out' : 'Add to bag'}
                  </CheckoutButton>

                  <TrustRow>
                    <span>
                      <Inventory2Outlined /> Small batch, hand checked
                    </span>
                    <span>
                      <SpaOutlined /> Naturally derived
                    </span>
                    <span>
                      <VerifiedOutlined /> Dermatologically minded
                    </span>
                  </TrustRow>
                </CardContent>
              </ProductCard>
            </Reveal>
          </Grid>
        </Grid>
      </Container>

      {isSolo && (
        <Box sx={{ mt: { xs: 6, md: 9 } }}>
          <Reveal from="up">
            <Container maxWidth="md">
              <TapeCaption>
                <CategoryTag>Real results</CategoryTag>
                <h3>Before &amp; after, two minutes apart</h3>
                <p>Unretouched photos from our community.</p>
              </TapeCaption>
            </Container>
          </Reveal>
          <Reveal from="fade" delay={120}>
            <ImageTape items={BEFORE_AFTER} />
          </Reveal>
        </Box>
      )}
    </ProductSection>
  );
};

export default ProductShowcase;
